/**
 * An email as it was sent — `docs/activity.md` §7.10 (Aaron, Oct 24, note
 * 2.6: "It should be able to display html email signatures like any web email
 * client").
 *
 * The HTML is asked for when the card is drawn (`GET …/messages/:id/html`,
 * kept for the page's life — it never changes) and drawn **in a sandboxed
 * frame, never into the page**, the email log's rule (AGENTS, "The audit
 * logs"). The sandbox has no `allow-scripts`, so nothing in it can run, and
 * no `allow-forms` or `allow-top-navigation`, so nothing in it can post or
 * move this page. It does carry three permissions the log's frame does not,
 * each for one reason:
 *
 *  - `allow-same-origin`, so this page can measure the frame and make it as
 *    tall as the email — a mail client's card is the message's height, not a
 *    360px window to scroll inside a scrolling thread. Without scripts in the
 *    frame that is all it grants: the content still cannot reach this page,
 *    and the sanitiser has already taken out every URL but `http(s)`,
 *    `mailto`, `tel` and the message's own images.
 *  - `allow-popups` and `allow-popups-to-escape-sandbox`, so a link in a
 *    signature opens in a new tab as it would in web mail (`<base
 *    target="_blank">`, and every link `rel="noopener noreferrer"`).
 *
 * **Images from the internet** show for a contact — a signature's logo is
 * nearly always one, and hiding them is what made signatures look broken —
 * and are off, behind **Show images**, for somebody Rowted doesn't know,
 * where a remote image is more likely a tracking pixel than a logo. The
 * quoted history folds behind "…", as web mail does.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { hideRemoteImages, type MessageHtml, type ThreadMessage } from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { activityRequest } from '../api.js'

const cache = new Map<string, MessageHtml>()

/** The frame's own page: the email on paper, as its sender drew it, with links that open beside this one. */
function frameDocument(html: string): string {
  return '<!doctype html><html><head><meta charset="utf-8"><base target="_blank">'
    + '<style>:root{color-scheme:light}html,body{margin:0;padding:0;background:Canvas;color:CanvasText}'
    + 'body{font-family:"Instrument Sans","Helvetica Neue",Arial,sans-serif;font-size:13px;line-height:1.5;'
    + 'overflow-wrap:anywhere;padding:1px 0}'
    + 'img{max-width:100%;height:auto}table{max-width:100%}pre{white-space:pre-wrap}'
    + 'blockquote{margin:0 0 0 .8ex;padding-left:1ex;border-left:2px solid GrayText}</style></head>'
    + `<body>${html}</body></html>`
}

/** The frame, as tall as what is in it — measured on load and whenever its width changes. */
function EmailFrame({ srcDoc, title }: { srcDoc: string; title: string }) {
  const frame = useRef<HTMLIFrameElement>(null)
  const [height, setHeight] = useState(24)
  const measure = useCallback(() => {
    const document = frame.current?.contentDocument
    const body = document?.body
    if (!document || !body) return
    /*
     * The body, not the document: a document is never shorter than its
     * frame, so folding the history back would leave the card at its
     * unfolded height. A wide newsletter scrolls sideways inside the card,
     * so its bar is allowed for.
     */
    const sideways = document.documentElement.scrollWidth > document.documentElement.clientWidth ? 14 : 0
    const next = Math.ceil(body.scrollHeight) + sideways
    if (next > 0) setHeight(next)
  }, [])
  useEffect(() => {
    const element = frame.current
    if (!element) return
    let width = element.clientWidth
    const watcher = new ResizeObserver(() => {
      if (element.clientWidth === width) return
      width = element.clientWidth
      measure()
    })
    watcher.observe(element)
    return () => watcher.disconnect()
  }, [measure])
  return (
    <iframe
      ref={frame}
      title={title}
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      srcDoc={srcDoc}
      onLoad={measure}
      data-email-frame
      className="block w-full rounded-md border-0 bg-surface"
      style={{ height }}
    />
  )
}

export function EmailBody({
  conversationId, message, sender, unknownSender,
}: {
  conversationId: string
  message: ThreadMessage
  sender: string
  /** Somebody Rowted doesn't know: images from the internet start off. */
  unknownSender: boolean
}) {
  const [answer, setAnswer] = useState<MessageHtml | null>(() => cache.get(message.id) ?? null)
  const [failed, setFailed] = useState(false)
  const [full, setFull] = useState(false)
  const [images, setImages] = useState(!unknownSender)

  useEffect(() => {
    if (answer || !message.html) return
    let live = true
    activityRequest<MessageHtml>(`/conversations/${conversationId}/messages/${message.id}/html`)
      .then((found) => {
        cache.set(message.id, found)
        if (live) setAnswer(found)
      })
      .catch(() => { if (live) setFailed(true) })
    return () => { live = false }
  }, [answer, conversationId, message.id, message.html])

  /* Until it comes — or if it never does — the words, as every email showed before. */
  if (!answer || failed) {
    return <div className="mt-1.5 whitespace-pre-wrap break-words text-[13px] leading-[1.5] text-text-body">{message.body}</div>
  }
  const folded = answer.quoteAt !== null && !full
  const shown = folded ? answer.html.slice(0, answer.quoteAt!) : answer.html
  const drawn = images ? { html: shown, hidden: 0 } : hideRemoteImages(shown)
  return (
    <div className="mt-1.5">
      {drawn.hidden > 0 && (
        <div className="mb-1.5 flex items-center gap-2 rounded-md bg-surface-alt px-2.5 py-1.5 text-[11.5px] text-text-muted">
          <span className="flex-1">Images are off for someone Rowted doesn’t know.</span>
          <button type="button" onClick={() => setImages(true)} className="font-semibold text-accent hover:text-accent-dark">
            Show images
          </button>
        </div>
      )}
      <EmailFrame srcDoc={frameDocument(drawn.html)} title={`Email from ${sender}`} />
      {answer.quoteAt !== null && (
        <button type="button" onClick={() => setFull((on) => !on)} aria-expanded={full}
          aria-label={full ? 'Hide the earlier messages' : 'Show the earlier messages'}
          title={full ? 'Hide the earlier messages' : 'Show the earlier messages'}
          className={cn('mt-1 inline-flex h-4 items-center rounded-[4px] bg-line-soft px-1.5 text-[11px] font-bold leading-none',
            'text-text-muted hover:bg-line hover:text-text')}>
          …
        </button>
      )}
    </div>
  )
}
